/**
 * The frame every Home gadget of components/home/gadgets shares: a `ph-card` with its title, a short note on the right
 * (`meta`), the links into the tabs (`links`, already gated by the plan) and the body. `GadgetBody` picks what the body
 * says while the data loads, when it failed or when there is nothing to show, so every gadget words those the same way.
 */
import React from 'react';
import PropTypes from 'prop-types';
import { ListRowsSkeleton } from '../../Skeletons';
import './Gadgets.css';

export default function GadgetCard({ title, testId, meta = null, links = null, children }) {
  return (
    <div className="ph-card gd-card" data-testid={testId}>
      <div className="ph-card-head">
        <h2 className="ph-card-title">{title}</h2>
        {meta && <span className="ph-muted ph-small">{meta}</span>}
        {links && <span className="ph-card-links">{links}</span>}
      </div>
      {children}
    </div>
  );
}

GadgetCard.propTypes = {
  children: PropTypes.node,
  links: PropTypes.node,
  meta: PropTypes.node,
  testId: PropTypes.string.isRequired,
  title: PropTypes.string.isRequired,
};

/** A link into a tab, or nothing when the plan hides it (`onClick` null). */
export function TabLink({ onClick, children, testId = undefined }) {
  if (!onClick) { return null; }
  return <button className="ph-link" data-testid={testId} onClick={onClick} type="button">{children}</button>;
}

TabLink.propTypes = { children: PropTypes.node, onClick: PropTypes.func, testId: PropTypes.string };

/** One quiet line (loading, an error, nothing yet). */
export const Quiet = ({ children, testId = undefined }) => <p className="ph-quiet" data-testid={testId}>{children}</p>;
Quiet.propTypes = { children: PropTypes.node, testId: PropTypes.string };

/**
 * While a gadget's first answer loads: grey rows where its content will be (the same shimmer as the rest of Flow), so
 * the card keeps its shape and does not jump; `label` is what a screen reader hears ("Reading the jobs…").
 */
export const GadgetSkeleton = ({ label = 'Loading', rows = 3, testId = 'gadget-loading' }) => (
  <div className="gd-skeleton" data-testid={testId} role="status">
    <ListRowsSkeleton label={label} rows={rows} />
  </div>
);
GadgetSkeleton.propTypes = { label: PropTypes.string, rows: PropTypes.number, testId: PropTypes.string };

/**
 * The body for one answer: `loading` (and no data yet) → a placeholder (`loadingText` for screen readers); `error` (and no data) → `errorText`; `empty` →
 * `emptyText`; else `children()`. Data kept from an earlier answer stays shown when a refresh fails.
 */
export function GadgetBody({ loading, error, empty, data, loadingText, errorText, emptyText, children }) {
  if (data === null || data === undefined) {
    if (error) { return <Quiet testId="gadget-error">{errorText}</Quiet>; }
    if (loading) { return <GadgetSkeleton label={typeof loadingText === 'string' ? loadingText : 'Loading'} />; }
  }
  if (empty) { return <Quiet testId="gadget-empty">{emptyText}</Quiet>; }
  return children();
}

GadgetBody.propTypes = {
  children: PropTypes.func.isRequired,
  data: PropTypes.any,
  empty: PropTypes.bool,
  emptyText: PropTypes.node,
  error: PropTypes.any,
  errorText: PropTypes.node,
  loading: PropTypes.bool,
  loadingText: PropTypes.node,
};
